import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Printer, Shield, Feather, Phone, Mail, MapPin } from "lucide-react";

const DEFAULT_ADDRESS = "सिरसी नवापारा, मेन रोड, सूरजपुर, छत्तीसगढ़ — 497231";

export default function IDCardPage() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [owner, setOwner] = useState(null);

  useEffect(() => {
    api.get(`/staff/${id}`).then((r) => setS(r.data));
    api.get("/admin/owner-card").then((r) => setOwner(r.data)).catch(() => {});
  }, [id]);

  if (!s) return <div className="text-slate-500">लोड हो रहा है…</div>;

  const officeAddress = s.address || owner?.address || DEFAULT_ADDRESS;
  const officePhone = owner?.phone || "+91 8463860303";
  const issuerName = owner?.full_name || "JAKIR MOMIN";
  const issuerRole = "प्रधान संपादक · Chief Editor";
  const signatureUrl = owner?.signature_url;
  const rniNo = owner?.rni_no || "";

  const qrValue = JSON.stringify({
    org: "INDIAN-NEWS-21", id: s.staff_id, name: s.name,
    role: s.designation, valid: s.valid_till, issuer: issuerName,
  });

  return (
    <div data-testid="idcard-page">
      <div className="flex items-center justify-between mb-6 no-print">
        <Link to="/admin/staff" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F172A]">
          <ArrowLeft className="w-4 h-4"/>वापस
        </Link>
        <Button onClick={() => window.print()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="print-id-card-btn">
          <Printer className="w-4 h-4 mr-1.5"/>प्रिंट / PDF
        </Button>
      </div>

      <div id="printable-id-card" className="flex flex-wrap gap-6 items-start justify-center">
        {/* ============ FRONT ============ */}
        <PremiumCard>
          {/* Top masthead strip */}
          <div className="relative flex items-center gap-2 px-4 pt-3">
            <img src="/logo.png" alt="" className="w-10 h-10 rounded bg-white/95 object-contain shadow"/>
            <div className="flex-1 min-w-0">
              <div className="font-black text-white text-[15px] leading-none tracking-wider">INDIAN-NEWS-21</div>
              <div className="text-[7.5px] uppercase tracking-[0.25em] text-amber-300 mt-1 font-bold">भारत की आवाज़ · Press · Media</div>
            </div>
            <div className="text-right">
              <div className="text-[7px] uppercase tracking-widest text-amber-300/80">Press ID</div>
              <div className="text-[9px] font-black text-amber-300 font-mono">{s.staff_id}</div>
            </div>
          </div>
          <div className="absolute top-[52px] left-4 right-4 h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent"/>

          {/* Body */}
          <div className="relative flex gap-3 px-4 pt-4 pb-3" style={{ marginTop: 2 }}>
            {/* Photo */}
            <div className="shrink-0" style={{ width: 82, height: 108 }}>
              <div className="w-full h-full rounded-md overflow-hidden border-2 border-amber-400 bg-slate-800 shadow-lg">
                {s.photo_url ? (
                  <img src={s.photo_url} alt="" className="w-full h-full object-cover"/>
                ) : (s.name || "").toUpperCase().includes("JAKIR") ? (
                  <img src="/jakir-momin-boss.png" alt={s.name} className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500 text-[9px]">फोटो</div>
                )}
              </div>
              <div className="text-[7px] uppercase tracking-widest text-amber-300/80 text-center mt-1 flex items-center justify-center gap-1">
                <Shield className="w-2.5 h-2.5"/>Verified
              </div>
            </div>

            {/* Center info */}
            <div className="flex-1 min-w-0">
              <div className="text-[7.5px] uppercase tracking-[0.22em] text-amber-300/90 font-bold">Name · नाम</div>
              <div className="text-white text-[15px] font-black leading-tight tracking-wide" style={{ fontFamily: "Georgia, serif" }}>{s.name}</div>

              <div className="mt-1.5 text-[7.5px] uppercase tracking-[0.22em] text-amber-300/90 font-bold">Designation · पदनाम</div>
              <div className="text-white text-[10.5px] font-semibold leading-tight">{s.designation}</div>

              <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-2">
                <MiniInfo k="Valid" v={s.valid_till}/>
                <MiniInfo k="Blood" v={s.blood_group || "—"}/>
                <MiniInfo k="DOB" v={s.dob || "—"}/>
                <MiniInfo k="Phone" v={s.phone || "—"}/>
              </div>
            </div>

            {/* QR */}
            <div className="shrink-0 flex flex-col items-center gap-1" style={{ width: 70 }}>
              <div className="bg-white p-0.5 rounded">
                <QRCodeSVG value={qrValue} size={60} level="H" bgColor="#ffffff" fgColor="#0F172A"/>
              </div>
              <div className="text-[6.5px] uppercase tracking-widest text-amber-300/80">Scan · स्कैन</div>
            </div>
          </div>

          {/* Bottom strip */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-amber-600 via-amber-400 to-amber-600 text-[#0F172A] px-3 py-1 flex items-center justify-between text-[7px] font-bold uppercase tracking-widest">
            <span>Press · Authorised Journalist</span>
            <span>www.indian-news-21</span>
          </div>
        </PremiumCard>

        {/* ============ BACK ============ */}
        <PremiumCard variant="back">
          <div className="relative flex items-center gap-2 px-4 pt-3">
            <img src="/logo.png" alt="" className="w-8 h-8 rounded bg-white/95 object-contain"/>
            <div className="flex-1">
              <div className="font-black text-white text-[13px] leading-none tracking-wider">INDIAN-NEWS-21</div>
              <div className="text-[7px] uppercase tracking-widest text-amber-300 mt-0.5 font-bold">कार्यालय की जानकारी</div>
            </div>
          </div>
          <div className="absolute top-[46px] left-4 right-4 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"/>

          <div className="relative px-4 pt-3 pb-2 text-white flex flex-col" style={{ height: "calc(100% - 28px)", marginTop: 4 }}>
            {/* Office address */}
            <div className="space-y-1.5 text-[8.5px]">
              <div className="flex items-start gap-1.5">
                <MapPin className="w-2.5 h-2.5 mt-0.5 text-amber-300 shrink-0"/>
                <div>
                  <div className="text-amber-300/80 uppercase tracking-widest text-[6.5px] font-bold">Office · कार्यालय</div>
                  <div className="text-white leading-tight">{officeAddress}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Phone className="w-2.5 h-2.5 text-amber-300 shrink-0"/>
                <div className="text-white">{officePhone}</div>
              </div>
              {(owner?.email || s.email) && (
                <div className="flex items-center gap-1.5">
                  <Mail className="w-2.5 h-2.5 text-amber-300 shrink-0"/>
                  <div className="text-white text-[7.5px]">{s.email || owner?.email}</div>
                </div>
              )}
            </div>

            <div className="h-px w-full bg-amber-400/40 my-2"></div>

            {/* Rules */}
            <div>
              <div className="text-[6.5px] uppercase tracking-widest text-amber-300/80 font-bold mb-0.5">नियम व शर्तें</div>
              <ul className="text-[7px] text-white/85 leading-tight space-y-0.5">
                <li>• यह कार्ड केवल अधिकृत पत्रकारिता कार्य हेतु है।</li>
                <li>• खोने पर तुरन्त कार्यालय को सूचित करें।</li>
                <li>• वैधता समाप्त होने पर कार्ड लौटायें।</li>
                <li>• दुरुपयोग पर कठोर कार्यवाही होगी।</li>
              </ul>
            </div>

            {/* Signature block — pushed to bottom */}
            <div className="mt-auto pt-2">
              <div className="flex items-end justify-between gap-2">
                <div className="text-[6.5px] text-amber-300/70">
                  {rniNo && <div>RNI: {rniNo}</div>}
                  <div className="text-white/60 mt-0.5">Issued by · जारीकर्ता</div>
                </div>
                <div className="text-center">
                  <div className="h-10 flex items-end justify-center min-w-[90px]">
                    {signatureUrl ? (
                      <img
                        src={signatureUrl}
                        alt="sig"
                        style={{
                          maxHeight: 38,
                          filter: "brightness(0) saturate(100%) invert(78%) sepia(60%) saturate(700%) hue-rotate(0deg) brightness(105%) contrast(105%)",
                          mixBlendMode: "screen",
                        }}
                        className="object-contain"
                      />
                    ) : (
                      <div className="text-amber-300 text-[13px] italic font-bold leading-none"
                        style={{ fontFamily: "'Times New Roman', serif" }}>
                        J. Momin
                      </div>
                    )}
                  </div>
                  <div className="h-px w-full bg-amber-400/70"/>
                  <div className="text-[7px] font-black text-amber-300 tracking-wider italic mt-0.5"
                    style={{ fontFamily: "'Times New Roman', serif" }}>
                    {issuerName}
                  </div>
                  <div className="text-[6px] text-white/70">{issuerRole}</div>
                </div>
              </div>
            </div>
          </div>
        </PremiumCard>
      </div>

      <div className="mt-8 max-w-md text-xs text-slate-500 no-print mx-auto text-center">
        प्रिंट सुझाव: कार्ड आकार <b>3.375" × 2.125"</b> (मानक)। प्रिंट डायलॉग में "बैकग्राउंड ग्राफ़िक्स" चालू रखें।
      </div>

      <style>{`
        .id-card-premium {
          width: 340px; height: 214px; position: relative; overflow: hidden;
          border-radius: 10px; color: white;
          background: linear-gradient(135deg, #0F172A 0%, #1E293B 40%, #0F172A 100%);
          box-shadow: 0 10px 30px -8px rgba(15, 23, 42, 0.45), 0 4px 12px -2px rgba(0,0,0,0.2);
        }
        .id-card-premium::before {
          content: ""; position: absolute; inset: 0; pointer-events: none;
          background-image:
            repeating-linear-gradient(45deg, rgba(251,191,36,.35) 0 1px, transparent 1px 6px),
            repeating-linear-gradient(-45deg, rgba(251,191,36,.22) 0 1px, transparent 1px 10px);
          opacity: 0.10;
        }
        .id-card-premium::after {
          content: ""; position: absolute; inset: 6px; pointer-events: none;
          border: 1px solid rgba(251, 191, 36, 0.45); border-radius: 6px;
          box-shadow: inset 0 0 0 1px rgba(251, 191, 36, 0.15);
        }
        .id-card-premium .watermark-logo {
          position: absolute; right: 12px; bottom: 22px; opacity: 0.06;
          pointer-events: none;
        }
        @media print {
          @page { size: auto; margin: 0.2in; }
          body { background: white !important; }
          .no-print { display: none !important; }
          #printable-id-card { justify-content: flex-start !important; }
        }
      `}</style>
    </div>
  );
}

function PremiumCard({ children, variant = "front" }) {
  return (
    <div className="id-card-premium" style={{ fontFamily: "'Times New Roman', serif" }} data-testid={`idcard-${variant}`}>
      <img src="/logo.png" alt="" className="watermark-logo" style={{ width: 160, height: 160, objectFit: "contain" }}/>
      {children}
    </div>
  );
}

function MiniInfo({ k, v }) {
  return (
    <div className="min-w-0">
      <div className="text-[6.5px] uppercase tracking-widest text-amber-300/70 font-bold">{k}</div>
      <div className="text-white text-[9px] font-mono truncate">{v || "—"}</div>
    </div>
  );
}
